import { useEffect } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react';
import { Button, Text } from '@mercator-gcp/grout';
import { useForm, Controller } from 'react-hook-form';
import { CrossIcon } from 'components/icons/CrossIcon';
import { FlexRow } from 'components/layout/FlexRow';
import useUserAccount, { User } from 'hooks/useUserAccount';

const modalFooterStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: 'padding_16',
};

type AccountModalProps = {
  isOpen: boolean;
  onClose: () => void;
  setModalOpen: (value: boolean) => void;
  userData: any;
};

type FormValues = {
  firstName: string;
  lastName: string;
  timezone: string;
  jobTitle: string;
};

export const EditAccountDetails = ({
  isOpen,
  onClose,
  setModalOpen,
  userData,
}: AccountModalProps) => {
  const { editUser } = useUserAccount();

  const { handleSubmit, control, reset } = useForm<FormValues>({
    defaultValues: {
      firstName: userData?.first_name || '',
      lastName: userData?.last_name || '',
      timezone: userData?.timezone || '',
      jobTitle: userData?.job_title || '',
    },
  });

  const handleFormSubmit = (data: FormValues) => {
    const userObj: Pick<User, 'first_name' | 'last_name' | 'timezone' | 'job_title'> = {
      first_name: data.firstName,
      last_name: data.lastName,
      timezone: data.timezone,
      job_title: data.jobTitle,
    };
    editUser(userData?.id, userObj);
    onClose();
  };

  // reset form on unmount
  useEffect(() => {
    if (userData) {
      reset({
        firstName: userData?.first_name,
        lastName: userData?.last_name,
        timezone: userData?.timezone,
        jobTitle: userData?.job_title,
      });
    }
  }, [userData, reset]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <ModalOverlay />
      <ModalContent>
        <FlexRow sx={{ width: '100%', padding: '15px' }}>
          <FlexRow.Left>
            <Text.Lead>My Account</Text.Lead>
          </FlexRow.Left>
          <FlexRow.Right>
            <Box sx={{ cursor: 'pointer' }} onClick={() => setModalOpen(false)}>
              <CrossIcon stroke="#2C402E" opacity={0.2} />
            </Box>
          </FlexRow.Right>
        </FlexRow>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <ModalBody>
            <FormControl id="firstName">
              <FormLabel sx={{ fontSize: '12px', fontWeight: '600' }}>
                First Name
              </FormLabel>
              <Controller
                name="firstName"
                control={control}
                rules={{ required: true }}
                render={({ field }) => <Input type="text" {...field} />}
              />
            </FormControl>
            <FormControl id="lastName" mt={4}>
              <FormLabel sx={{ fontSize: '12px', fontWeight: '600' }}>
                Last Name
              </FormLabel>
              <Controller
                name="lastName"
                control={control}
                rules={{ required: true }}
                render={({ field }) => <Input type="text" {...field} />}
              />
            </FormControl>
            <FormControl id="jobTitle" mt={4}>
              <FormLabel sx={{ fontSize: '12px', fontWeight: '600' }}>
                Job Title
              </FormLabel>
              <Controller
                name="jobTitle"
                control={control}
                rules={{ required: false }}
                render={({ field }) => <Input type="text" {...field} />}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter sx={modalFooterStyle}>
            <Button variant="functional" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
